import { formatMoney, useGraphQLQuery } from '@betterment/js-runtime';
import { Accordion, Box, Button, Dialog, Heading, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { isNil } from 'es-toolkit/compat';

import { AllTimeTaxLossHarvestingPerformanceDocument } from './graphql/query.allTimeTaxLossHarvestingPerformance.generated';
import { CurrentYearTaxLossHarvestingPerformanceDocument } from './graphql/query.currentYearTaxLossHarvestingPerformance.generated';

import { urlBuilder } from '../../urlBuilder';
import { CompactCardLoader } from '../shimmer-loaders/compact-card-loader';

const TaxLossHarvestingPerformanceModalContent = () => {
  const { data } = useGraphQLQuery(AllTimeTaxLossHarvestingPerformanceDocument, { throwOnError: true });
  const { data: currentYearTaxLossesharvestedData } = useGraphQLQuery(CurrentYearTaxLossHarvestingPerformanceDocument, {
    throwOnError: true,
  });
  if (!data || !currentYearTaxLossesharvestedData) return <CompactCardLoader />;

  const currentYearTaxLossesHarvestedDisplayValue = isNil(
    currentYearTaxLossesharvestedData.performanceTotals.investing?.taxLossesHarvested
  )
    ? ''
    : formatMoney(currentYearTaxLossesharvestedData.performanceTotals.investing.taxLossesHarvested);
  const allTimeTaxSavingsDisplayValue = isNil(data.performanceTotals.investing?.allTimeTlhEstimatedTaxSavings)
    ? ''
    : formatMoney(data.performanceTotals.investing.allTimeTlhEstimatedTaxSavings);
  const allTimeHarvestedLossesDisplayValue = isNil(data.performanceTotals.investing?.taxLossesHarvested)
    ? ''
    : formatMoney(data.performanceTotals.investing.taxLossesHarvested);
  const estimatedTaxSavingsDescription = data.performanceTotals.investing?.tlhEstimatedTaxSavingsDescription || '';
  const { taxLossHarvestingExplanation } = data.disclosures;

  return (
    <>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
        <Text>
          We base these estimates on your financial profile including income, location, and tax rates.{' '}
          <SambaAutoLink to={urlBuilder.appProfile.absolute()} variant="textPrimary">
            Check your financial profile
          </SambaAutoLink>{' '}
          to ensure the most accurate estimates.
        </Text>

        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.150' }}>
          <Heading as="h3" variant="title200">
            All time
          </Heading>
          <Separator />
          <Box
            css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            data-testid="allTimeTaxSavings"
          >
            <Text>Estimated tax savings*</Text>
            <Text>{allTimeTaxSavingsDisplayValue}</Text>
          </Box>
          <Separator />
          <Box
            css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            data-testid="allTimeHarvestedLosses"
          >
            <Text>Harvested losses</Text>
            <Text>{allTimeHarvestedLossesDisplayValue}</Text>
          </Box>
          <Separator />
        </Box>

        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.150' }}>
          <Heading as="h3" variant="title200">
            This year
          </Heading>
          <Separator />
          <Box
            css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            data-testid="currentYearHarvestedLosses"
          >
            <Text>Harvested losses</Text>
            <Text>{currentYearTaxLossesHarvestedDisplayValue}</Text>
          </Box>
          <Separator />
        </Box>
        <Box>
          <Accordion type="multiple">
            <Accordion.Item value="item-1" css={{ borderBottom: '0px' }}>
              <Accordion.Trigger aria-label="what-are-harvested-losses" css={{ padding: 'space.0' }}>
                <Text>What are harvested losses?</Text>
              </Accordion.Trigger>
              <Accordion.Content>
                <Text
                  data-testid="taxLossHarvestingExplanation"
                  dangerouslySetInnerHTML={{ __html: taxLossHarvestingExplanation }}
                />
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
        </Box>
        <Text color="text.weak" variant="text25">
          <Text as="span" variant="text25">
            *
          </Text>{' '}
          <Text variant="text25" dangerouslySetInnerHTML={{ __html: estimatedTaxSavingsDescription }} />
        </Text>
      </Box>
    </>
  );
};

export const TaxLossHarvestingPerformanceModal = () => {
  return (
    <Dialog overlay="default">
      <Dialog.Trigger data-samba-component={true} asChild={true}>
        <Button variant="textPrimary" aria-label="See details of tax loss harvesting">
          See details
        </Button>
      </Dialog.Trigger>
      <Dialog.Content
        css={{
          padding: 'space.400',
          width: '100%',
          maxWidth: 'container.large',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        <Dialog.Title asChild={true}>
          <Heading as="h2" variant="title300" css={{ mb: 'space.200' }}>
            Your estimated tax savings
          </Heading>
        </Dialog.Title>
        <Dialog.Description asChild={true}>
          <TaxLossHarvestingPerformanceModalContent />
        </Dialog.Description>
      </Dialog.Content>
    </Dialog>
  );
};
